export const PRIMARY_STORAGE_KEY = "tesla-arcade:state:v1";
export const BACKUP_STORAGE_KEY = "tesla-arcade:state:v1:backup";

export interface GameStats {
  launches: number;
  matchesStarted: number;
  matchesCompleted: number;
  totalPlayMs: number;
  lastPlayedAt: number | null;
  lastCompletedAt: number | null;
}

export interface ArcadeStateV1 {
  schemaVersion: 1;
  preferences: {
    soundEnabled: boolean;
    masterVolume: number;
    sortMode: "frequency";
  };
  games: Record<string, GameStats>;
  writtenAt: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && typeof value === "number" && value >= 0;
}

function isTimestamp(value: unknown): value is number | null {
  return value === null || isNonNegativeInteger(value);
}

function validateGameStats(value: unknown): GameStats | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  if (
    !isNonNegativeInteger(value.launches) ||
    !isNonNegativeInteger(value.matchesStarted) ||
    !isNonNegativeInteger(value.matchesCompleted) ||
    !isNonNegativeInteger(value.totalPlayMs) ||
    !isTimestamp(value.lastPlayedAt) ||
    !isTimestamp(value.lastCompletedAt)
  ) {
    return undefined;
  }

  return {
    launches: value.launches,
    matchesStarted: value.matchesStarted,
    matchesCompleted: value.matchesCompleted,
    totalPlayMs: value.totalPlayMs,
    lastPlayedAt: value.lastPlayedAt,
    lastCompletedAt: value.lastCompletedAt,
  };
}

export function createDefaultState(): ArcadeStateV1 {
  return {
    schemaVersion: 1,
    preferences: {
      soundEnabled: true,
      masterVolume: 1,
      sortMode: "frequency",
    },
    games: {},
    writtenAt: 0,
  };
}

export function cloneState(state: ArcadeStateV1): ArcadeStateV1 {
  return {
    schemaVersion: 1,
    preferences: { ...state.preferences },
    games: Object.fromEntries(
      Object.entries(state.games).map(([id, stats]) => [id, { ...stats }]),
    ),
    writtenAt: state.writtenAt,
  };
}

export function parseArcadeState(value: unknown): ArcadeStateV1 | undefined {
  if (!isRecord(value) || value.schemaVersion !== 1 || !isRecord(value.preferences)) {
    return undefined;
  }

  const preferences = value.preferences;
  if (
    typeof preferences.soundEnabled !== "boolean" ||
    typeof preferences.masterVolume !== "number" ||
    !Number.isFinite(preferences.masterVolume) ||
    preferences.masterVolume < 0 ||
    preferences.masterVolume > 1 ||
    preferences.sortMode !== "frequency" ||
    !isRecord(value.games) ||
    !isNonNegativeInteger(value.writtenAt)
  ) {
    return undefined;
  }

  const games: Record<string, GameStats> = {};
  for (const [id, gameValue] of Object.entries(value.games)) {
    const gameStats = validateGameStats(gameValue);
    if (!gameStats) {
      return undefined;
    }
    games[id] = gameStats;
  }

  return {
    schemaVersion: 1,
    preferences: {
      soundEnabled: preferences.soundEnabled,
      masterVolume: preferences.masterVolume,
      sortMode: "frequency",
    },
    games,
    writtenAt: value.writtenAt,
  };
}
